import { createSelector } from 'reselect';

import { getOrderData, getOrderItems } from 'dux/checkoutOrder/selectors';
import { getCartData, getItems as getCartItems } from 'dux/checkoutCart/selectors';
import { getGiftContent } from 'dux/gift/selectors';
import * as feautreFlagSelectors from 'dux/featureFlags/selectors';
import { getUserProfile, getUserPubkey } from 'dux/user/selectors';

import { productsSlugs } from 'constants/products';

// get all order items that are of type formula (exclude coupons)
// enhance with subscription data from cart
// Note: this might be simplified if subscription information were available on the order items
const getOrderFormulaItems = createSelector(getOrderItems, getCartItems, (items, cartItems) =>
  items
    .filter(item => item.variant?.is_formula || item.variant?.product?.type === productsSlugs.BRUSH)
    .map(item => {
      const correspondingCartItem = cartItems.find(
        cartItem => cartItem.type === item.variant?.product?.type
      );
      return {
        ...item,
        sku: correspondingCartItem?.sku,
        subscription: correspondingCartItem?.subscription?.active || false,
        subscriptionFrequency: correspondingCartItem?.subscription?.frequency,
      };
    })
);

// Get gift (as a gifter)
const getOrderGiftset = createSelector(getOrderItems, items =>
  items.filter(item => item.variant?.product?.type === productsSlugs.DIGITAL_GIFT)
);

// --------------------- GTM ------------------------
const getGtmProducts = createSelector(
  getOrderFormulaItems,
  getOrderGiftset,
  getGiftContent,
  feautreFlagSelectors.shouldShowShimmerHairOil,
  feautreFlagSelectors.shouldShowSupplements,
  feautreFlagSelectors.shouldShowSunsetAgaveBrush,
  (formulaItems, giftset, giftContent, showShimmerHairOil, showSupplements, hideAgaveBrush) => {
    // Keep nomenclature from old OMS to prevent analytics discrepancies
    const nameMap = {
      [productsSlugs.CONDITIONER]: 'Conditioner',
      [productsSlugs.DRY_SHAMPOO]: 'Dry Shampoo',
      [productsSlugs.CURL_CREAM]: 'Curl Cream',
      [productsSlugs.LEAVEIN_CONDITIONER]: 'Leave-in Conditioner',
      [productsSlugs.MASK]: 'Mask',
      [productsSlugs.HAIR_MASK]: 'Pre-Shampoo Hair Mask',
      [productsSlugs.SCALP_MASK]: 'Pre-Shampoo Scalp Mask',
      [productsSlugs.SHAMPOO]: 'Shampoo',
      [productsSlugs.OIL]: 'Hair Oil',
      ...(showShimmerHairOil ? { [productsSlugs.SHIMMER_HAIR_OIL]: 'Shimmer Hair Oil' } : {}),
      ...(showSupplements ? { [productsSlugs.SUPPLEMENT_CORE]: 'Supplements' } : {}),
      [productsSlugs.TRIAL_SHAMPOO]: 'Trial Shampoo',
      [productsSlugs.TRIAL_CONDITIONER]: 'Trial Conditioner',
      [productsSlugs.BRUSH_BOAR]: 'Boar Bristle Brush',
      ...(!hideAgaveBrush ? { [productsSlugs.BRUSH_AGAVE]: 'Agave Fiber Bristle Brush' } : {}),
      [productsSlugs.BRUSH_MIXED]: 'Boar & Nylon Bristle Brush',
      [productsSlugs.BRUSH_DETANGLING]: 'Tipped-Metal Bristle Brush',
      [productsSlugs.ESSENTIALS_SET]: 'The Essentials',
      [productsSlugs.COMPLETE_SET]: 'The Complete Set',
    };

    const formulaProducts = formulaItems.map(item => {
      return {
        brand: 'Prose',
        SKU: item.sku ? item.sku : item.pubkey,
        category: item.item_object?.gtm_category,
        name: nameMap[item.variant?.product?.type] || nameMap[item.variant?.type] || item.type,
        price: item.total_price,
        priceLessTax: item.total_price,
        quantity: item.quantity,
        variant: item.item_object?.gtm_variant,
        subscription: item.subscription,
        subscriptionFrequency: item.subscriptionFrequency,
      };
    });

    const giftsetProducts = giftset.map(item => {
      return {
        brand: 'Prose',
        SKU: item.pubkey,
        category: giftContent.form.occasion.find(o => o.value === item.item_object.occasion).label,
        name: nameMap[item.variant?.type] || item.variant?.label,
        price: item.total_price,
        priceLessTax: item.total_price,
        quantity: item.quantity,
        variant: 'Gift Set',
      };
    });

    return [...formulaProducts, ...giftsetProducts];
  }
);

export const getGtmPaymentDetails = createSelector(
  getUserPubkey,
  getOrderData,
  getCartData,
  getUserProfile,
  getGtmProducts,
  (userPubkey, order, cart, profile, products) => {
    const purchase = order && {
      actionField: {
        id: order.serial_no,
        customerID: userPubkey,
        // order count is incremented just after purchase so new user has order_count=1 here
        customerStatus: profile?.order_count > 1 ? 'Existing' : 'New',
        discountCode: '',
        discountAmount: order.total_price - order.total_amount,
        affiliation: 'Prose-D2C',
        revenue: order.total_price,
        tax: order.total_taxes,
        canSubscribeAtCart: cart.can_subscribe,
        isSubscription: order.is_subscription,
      },
      products,
    };

    return {
      event: 'purchase',
      ecommerce: { purchase },
    };
  }
);
// --------------------------------------------------
